import * as React from "react";
import typia from "typia";

import { assert } from "$app/utils/assert";

export type TeamMembership = {
  id: string;
  seller_name: string;
  seller_avatar_url: string;
  has_some_read_only_access: boolean;
  is_selected: boolean;
};

type Policies = {
  affiliate_requests_onboarding_form: {
    update: boolean;
  };
  direct_affiliate: {
    create: boolean;
    update: boolean;
  };
  collaborator: {
    create: boolean;
    update: boolean;
  };
  product: {
    create: boolean;
  };
  product_review_response: {
    update: boolean;
  };
  balance: {
    index: boolean;
    export: boolean;
  };
  checkout_offer_code: {
    create: boolean;
  };
  checkout_form: {
    update: boolean;
  };
  upsell: {
    create: boolean;
  };
  settings_payments_user: {
    show: boolean;
  };
  settings_main_user: {
    update_username: boolean;
  };
  settings_profile: {
    manage_social_connections: boolean;
    update: boolean;
  };
  settings_third_party_analytics_user: {
    update: boolean;
  };
  installment: {
    create: boolean;
  };
  workflow: {
    create: boolean;
  };
  utm_link: {
    index: boolean;
  };
  community: {
    index: boolean;
  };
  churn: {
    show: boolean;
  };
  page: {
    index: boolean;
    create: boolean;
  };
  user: {
    view_store_agent: boolean;
    use_store_agent: boolean;
  };
};

export type LoggedInUser = {
  id: string;
  email: string | null;
  name: string | null;
  avatarUrl: string;
  confirmed: boolean;
  teamMemberships: TeamMembership[];
  canCreateBrandAccount: boolean;
  hasPayoutSetupToPort: boolean;
  policies: Policies;
  /**
   * Dashboard nav destinations this user has earned (see DashboardNav). Anything not listed here and
   * not in the core set renders under "Everything else" until they use it.
   */
  promotedNavItems: string[];
  /**
   * This is a temporary feature flag to enable lazy loading for offscreen images on discover page
   * It should be removed once lazy loading is fully rolled out.
   */
  lazyLoadOffscreenDiscoverImages: boolean;
};

const Context = React.createContext<LoggedInUser | null | undefined>(undefined);

export const parseLoggedInUser = (data: unknown): LoggedInUser | null => {
  const parsed = typia.assert<{
    id: string;
    email: string | null;
    name: string | null;
    avatar_url: string;
    team_memberships: TeamMembership[];
    can_create_brand_account: boolean;
    has_payout_setup_to_port: boolean;
    policies: Policies;
    promoted_nav_items: string[];
    confirmed: boolean;
    lazy_load_offscreen_discover_images: boolean;
  } | null>(data);
  if (parsed == null) return null;
  return {
    id: parsed.id,
    email: parsed.email,
    name: parsed.name,
    avatarUrl: parsed.avatar_url,
    confirmed: parsed.confirmed,
    teamMemberships: parsed.team_memberships,
    canCreateBrandAccount: parsed.can_create_brand_account,
    hasPayoutSetupToPort: parsed.has_payout_setup_to_port,
    policies: parsed.policies,
    promotedNavItems: parsed.promoted_nav_items,
    lazyLoadOffscreenDiscoverImages: parsed.lazy_load_offscreen_discover_images,
  };
};

export const useLoggedInUser = (): LoggedInUser | null => {
  const value = React.useContext(Context);
  assert(value !== undefined, "Cannot read logged-in user, make sure LoggedInUserProvider is used");
  return value;
};

export const LoggedInUserProvider = Context.Provider;
